import { AxiosError } from 'axios'
import { processDownloadedData } from 'components/providers/GetChangesProvider'
import dayjs from 'dayjs'
import Api from 'services/Api'
import Database from 'services/db/Database'
import { MasterLabel } from 'services/db/Queries/MasterLabel'
import Logger from 'services/Logger'
import {
  ChangeDocsByTypes,
  MasterLabelDoc,
  MasterLabelLabel
} from 'types/api/changes'

const ADD_MASTER_LABEL_URL = '/misc/masterlabels/add'
const EDIT_MASTER_LABEL_URL = '/misc/masterlabels/edit'
export const API_GET_MASTER_LABEL = '/misc/masterlabels'

/**
 * Creates a ChangeDocsByTypes object from a MasterLabelDoc
 */
function createDocsFromMasterLabel(
  masterLabel: MasterLabelDoc
): ChangeDocsByTypes {
  if (!masterLabel._id) throw new Error('MasterLabel has no ID')
  return {
    Group: {},
    GroupEvent: {},
    Account: {},
    MasterLabel: {
      [masterLabel._id]: masterLabel
    },
    Profile: {},
    UserSettings: {},
    Task: {},
    Note: {}
  }
}

/**
 * Handles the response from API calls that return MasterLabelDoc
 */
async function handleMasterLabelResponse(result: any) {
  const masterLabel: MasterLabelDoc = result.data

  if (masterLabel._id) {
    const docs = createDocsFromMasterLabel(masterLabel)
    await processDownloadedData(docs)
    return masterLabel
  }
  return null
}

/**
 * Removes a label from the master label
 */
export async function removeLabelFromMasterLabel(
  toRemove: MasterLabelLabel
): Promise<void> {
  try {
    Logger.blue('MasterLabel:Remove', toRemove)

    // Get current master label and update it locally
    const masterLabel = await MasterLabel()
    const updatedMasterLabel = await Database.write(async () => {
      return masterLabel.update((local) => {
        const filteredOut = local.Labels.filter(
          (label) => label.ID !== toRemove.ID
        )
        Logger.blue('MasterLabel:Remove filtered labels', filteredOut)
        local.Labels = filteredOut
      })
    })

    // Send updated master label to the server
    Logger.blue('MasterLabel:Updated', updatedMasterLabel)
    const result = await Api.post(
      `${EDIT_MASTER_LABEL_URL}/${updatedMasterLabel._id}`,
      { ...Database.toServerObject(updatedMasterLabel) }
    )

    // Handle server response
    Logger.blue('MasterLabel:Upload', result)
    await handleMasterLabelResponse(result)
  } catch (error) {
    Logger.red('Failed to remove label from master label', error)
    throw error
  }
}

/**
 * Edits a label in the master label
 */
export async function editLabelInMasterLabel(
  updatedLabel: MasterLabelLabel
): Promise<void> {
  try {
    // Get master label
    const ml = await MasterLabel()

    // Create updated labels array
    const updatedLabels = ml.Labels.map((label) =>
      label.ID === updatedLabel.ID ? updatedLabel : label
    )

    try {
      // First attempt to update the master label
      const result = await Api.post(`${EDIT_MASTER_LABEL_URL}/${ml._id}`, {
        ...Database.toServerObject(ml),
        Labels: updatedLabels
      })

      Logger.blue('Updated MasterLabel', result)
      await handleMasterLabelResponse(result)
    } catch (error) {
      // Handle conflict error (409)
      if (error instanceof AxiosError && error.response?.status === 409) {
        handle409(error, ml, updatedLabels)
      } else {
        // Re-throw other errors
        throw error
      }
    }
  } catch (error) {
    Logger.red('Failed to edit label in master label', error)
    throw error
  }
}

async function handle409(
  error: AxiosError,
  ml: MasterLabelDoc,
  updatedLabels: MasterLabelLabel[]
) {
  Logger.red('CONFLICT MASTER LABEL', error.response?.data)

  // Get the latest data from the server
  const response = await Api.get(API_GET_MASTER_LABEL)
  const serverMasterLabel = response.data as MasterLabelDoc

  if (serverMasterLabel._id) {
    // Process the latest data
    const docs = createDocsFromMasterLabel(serverMasterLabel)
    await processDownloadedData(docs)

    // Try to update again with the latest data
    const retryResult = await Api.post(`${EDIT_MASTER_LABEL_URL}/${ml._id}`, {
      ...Database.toServerObject(ml),
      Labels: updatedLabels
    })

    Logger.orange('Updated after conflict', retryResult)
    await handleMasterLabelResponse(retryResult)
  }
}

/**
 * Adds a new label to the master label
 */
export async function addLabelToMasterLabel(
  title: string,
  color: string
): Promise<void> {
  // Create new label object
  const newLabel: MasterLabelLabel = {
    ID: dayjs().valueOf().toString(),
    Text: title,
    Color: color
  }

  // Get master label
  const ml = await MasterLabel()

  // Create updated labels array (filter out any temp labels)
  const updatedLabels = [
    ...ml.Labels.filter((label) => label.ID !== 'temp'),
    newLabel
  ]
  try {
    // Send updated master label to the server
    const result = await Api.post(`${EDIT_MASTER_LABEL_URL}/${ml._id}`, {
      ...Database.toServerObject(ml),
      Labels: updatedLabels
    })

    // Handle server response
    Logger.blue('Added label to master label', result)
    await handleMasterLabelResponse(result)
  } catch (error) {
    // Handle conflict error (409)
    if (error instanceof AxiosError && error.response?.status === 409) {
      handle409(error, ml, updatedLabels)
    } else {
      // Re-throw other errors
      throw error
    }
  }
}

export function convertHexToRGBALabelColor(hex: string) {
  // Remove the '#' character from the beginning of the hex string
  const cleanedHex = hex.replace('#', '')

  // Check if the hex string is valid
  if (!/^([0-9A-F]{3}){1,2}$/i.test(cleanedHex)) {
    return '{0,0,0,1}' // Invalid hex string
  }

  if (cleanedHex && cleanedHex.match(/.{1,2}/g)) {
    // Parse the hex string into separate RGB components
    const hexMatches = cleanedHex.match(/.{1,2}/g)
    const [r, g, b] = hexMatches
      ? hexMatches.map((value) => parseInt(value, 16))
      : [0, 0, 0]

    // Construct the RGBA string
    return `{${r / 255},${g / 255},${b / 255},${1}}`
  }

  return '{0,0,0,1}'
}
